import { lazy, Suspense } from 'react'
import type { ReactNode } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { NumberTicker } from '@/components/motion/number-ticker'
import { chartVarsClassName } from '@/components/spectrumui/charts/chart-kit'
import type { TrajectoryPoint } from './trajectory-chart'

export type { TrajectoryPoint } from './trajectory-chart'

/**
 * Shared tiles for the two dashboard homes (candidate + recruiter), which
 * render the same full-viewport mosaic grid (.dash-full in aura-app.css)
 * with different data. Kept free of domain logic: callers own derivation
 * and chip/label semantics.
 */

// recharts (and its d3 deps) is the heaviest thing in the bundle, and the
// dashboard is the landing screen — the chart loads as its own chunk while
// the stat cards paint. Empty arcs don't even fetch it.
const TrajectoryChartLazy = lazy(() =>
  import('./trajectory-chart').then(m => ({ default: m.TrajectoryChart })),
)
// Same deal for the stat-card sparkline: rides in the chart chunk, only
// fetched when a stat actually carries a series.
const SparklineLazy = lazy(() =>
  import('@/components/spectrumui/charts/sparkline-chart').then(m => ({ default: m.Sparkline })),
)

const ARROW_OUT = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8"/></svg>

export function StatCard(props: { label: string; value: ReactNode; hint: ReactNode; featured?: boolean; series?: number[] }) {
  return (
    <article className={`dash-stat${props.featured ? ' is-featured' : ''}`}>
      <div className="dash-stat-top">
        <span className="dash-label">{props.label}</span>
        <span className="dash-circle" aria-hidden="true">{ARROW_OUT}</span>
      </div>
      <strong className="dash-stat-value">
        {/* Numeric values roll in on first paint; the sr-only text inside the
            ticker keeps the raw number readable to tests and screen readers. */}
        {typeof props.value === 'number'
          ? <NumberTicker value={props.value} startOnView={false} />
          : props.value}
      </strong>
      <span className="dash-stat-hint">{props.hint}</span>
      {/* Only real series make it here (callers pass score history); the tile
          keeps its height when a stat has no trend behind it. The chart vars
          class supplies --spectrum-chart-up/down — without it the stroke
          resolves to nothing and the line vanishes. */}
      {props.series && props.series.length > 1 && (
        <Suspense fallback={null}>
          <SparklineLazy
            data={props.series.map((value, i) => ({ i, value }))}
            filled
            framed={false}
            className={`dash-stat-spark ${chartVarsClassName}`}
          />
        </Suspense>
      )}
    </article>
  )
}

/**
 * Score trajectory as an interactive area chart (recharts on the Spectrum
 * chart-kit primitives): hover any point for its date and score, the area
 * fills in the accent gradient. The empty-arc text stays synchronous so a
 * fresh dashboard never fetches the chart chunk at all.
 */
export function TrajectoryChart({ arc }: { arc: TrajectoryPoint[] }) {
  if (arc.length === 0) {
    return <div className="dash-line-empty">No scored runs yet — your line starts with the first report.</div>
  }
  return (
    // Skeleton instead of an empty div: the chart chunk loads async, and a
    // pulsing silhouette keeps the tile from popping when it lands.
    <Suspense fallback={<div className="dash-line" aria-hidden="true"><Skeleton className="h-full w-full" /></div>}>
      <TrajectoryChartLazy arc={arc} />
    </Suspense>
  )
}

/** Mosaic-shaped loading state, so the grid holds its silhouette while data loads. */
export function DashSkeleton() {
  return (
    <div className="dash-skeleton" role="status" aria-live="polite">
      <div className="dash-skeleton-row">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="dash-skeleton-stat" />
        ))}
      </div>
      <div className="dash-skeleton-row dash-skeleton-main">
        <Skeleton className="dash-skeleton-tile" />
        <Skeleton className="dash-skeleton-tile" />
      </div>
    </div>
  )
}
